import { httpClient } from '@/utils/httpClient';
import type { NotifyEnvelope } from '@/types/api';
import { getAccountById } from '@/features/accounts/services/accountService';
import type { Group, GroupMember } from '@/features/accounts/types/account';

export interface AccountOption {
  id: string;
  name: string;
}

export interface ApplicationOption {
  id: string;
  name: string;
}

export interface GroupRow {
  id: string;
  groupName: string;
  accountId: string;
  accountName: string;
  applicationId: string;
  applicationName: string;
}

interface AccountListItem {
  id: string;
  accountName: string;
}

export interface GroupDetail {
  id: string;
  groupName: string;
  accountId: string;
  applicationName: string;
  members: GroupMember[];
}

export interface AddGroupItem {
  groupName: string;
  members?: GroupMember[];
}

export interface AddGroupPayload {
  accountId: string;
  applicationName: string;
  groups: AddGroupItem[];
}

export interface UpdateGroupPayload {
  groupName: string;
  members?: GroupMember[];
}

let inflightAccountsList: Promise<AccountOption[]> | null = null;
let cachedAccounts: AccountOption[] | null = null;

export const getAccountsList = (): Promise<AccountOption[]> => {
  if (cachedAccounts) return Promise.resolve(cachedAccounts);
  if (inflightAccountsList) return inflightAccountsList;
  inflightAccountsList = httpClient
    .get<NotifyEnvelope<AccountListItem[]>>('/account')
    .then(({ data }) => {
      const accounts: AccountOption[] = (data.Response ?? []).map((a) => ({
        id: a.id,
        name: a.accountName,
      }));
      cachedAccounts = accounts;
      return accounts;
    })
    .catch((err) => {
      inflightAccountsList = null;
      throw err;
    });
  return inflightAccountsList;
};

export const getAllGroups = async (
  accountId: string,
  applicationId: string,
): Promise<Group[]> => {
  const detail = await getAccountById(accountId);
  const appGroup = (detail.groupResponse ?? []).find(
    (g) => g.applicationId === applicationId,
  );
  return appGroup?.groups ?? [];
};

const inflightAppsByAccount = new Map<string, Promise<ApplicationOption[]>>();

export const getApplicationsByAccount = (
  accountId: string,
): Promise<ApplicationOption[]> => {
  const hit = inflightAppsByAccount.get(accountId);
  if (hit) return hit;

  type AppItem = { id: string; name: string };
  const promise = httpClient
    .get<NotifyEnvelope<{ applications: Array<AppItem | string> }>>(
      '/v1/accountData/applications',
      { headers: { 'account-id': accountId } },
    )
    .then(({ data }) => {
      const items = data.Response?.applications ?? [];
      return items.map<ApplicationOption>((a) =>
        typeof a === 'string'
          ? { id: a, name: a }
          : { id: a.id, name: a.name },
      );
    });

  inflightAppsByAccount.set(accountId, promise);
  promise.finally(() => inflightAppsByAccount.delete(accountId));
  return promise;
};

const inflightGroupById = new Map<string, Promise<GroupDetail>>();

export const getGroupById = (groupId: string): Promise<GroupDetail> => {
  const hit = inflightGroupById.get(groupId);
  if (hit) return hit;

  const promise = (async () => {
    const { data } = await httpClient.get<NotifyEnvelope<GroupDetail>>(
      `/group/${encodeURIComponent(groupId)}`,
    );
    const detail = data.Response;
    return {
      id: detail.id,
      groupName: detail.groupName,
      accountId: detail.accountId,
      applicationName: detail.applicationName,
      members: detail.members ?? [],
    };
  })();

  inflightGroupById.set(groupId, promise);
  promise.finally(() => inflightGroupById.delete(groupId));
  return promise;
};

export const addGroup = async (payload: AddGroupPayload): Promise<void> => {
  await httpClient.post<NotifyEnvelope<unknown>>('/group', payload);
};

export const updateGroup = async (
  groupId: string,
  payload: UpdateGroupPayload,
): Promise<void> => {
  await httpClient.put<NotifyEnvelope<unknown>>(
    `/group/${encodeURIComponent(groupId)}`,
    payload,
  );
};

export const deleteGroup = async (groupId: string): Promise<void> => {
  await httpClient.delete<NotifyEnvelope<unknown>>(
    `/group/${encodeURIComponent(groupId)}`,
  );
};

export const syncGroups = async (
  accountId: string,
  applicationName: string,
  original: Group[],
  next: Group[],
): Promise<void> => {
  // Pre-flight: the final group set must have unique names (case-insensitive).
  const normalized = next.map((g) => g.groupName.trim().toLowerCase());
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (let i = 0; i < normalized.length; i++) {
    const n = normalized[i];
    if (seen.has(n)) dupes.add(next[i].groupName.trim());
    seen.add(n);
  }
  if (dupes.size > 0) {
    throw new Error(
      `Duplicate group name(s): ${Array.from(dupes).join(', ')}`,
    );
  }

  const nextById = new Set(
    next.map((g) => g.groupId).filter((id): id is string => Boolean(id)),
  );

  const toDelete = original.filter(
    (g): g is Group & { groupId: string } =>
      Boolean(g.groupId) && !nextById.has(g.groupId!),
  );
  const toAdd = next.filter((g) => !g.groupId);
  const toUpdate = next.filter(
    (g): g is Group & { groupId: string } => Boolean(g.groupId),
  );

  for (const g of toDelete) {
    await deleteGroup(g.groupId);
  }
  if (toAdd.length > 0) {
    await addGroup({
      accountId,
      applicationName,
      groups: toAdd.map((g) => ({
        groupName: g.groupName,
        members: g.members,
      })),
    });
  }
  for (const g of toUpdate) {
    await updateGroup(g.groupId, {
      groupName: g.groupName,
      members: g.members,
    });
  }
};
